import React, { useState, useEffect, useMemo } from "react";

import logo1 from "../Assets/logo1.jpg";

import TradeEditModal from "./TradeEditModal";

import {
  formatCurrency,
  formatDate,
  getEFTTypeDisplayName,
  getPaymentPlanDisplayName,
} from "../utils/eftUtils";

import axiosInstance from "../config/axios";
import { getTradeTaxRate } from "../constants/tax";
import { roundTo2 } from "../utils/numberUtils";
import {
  buildAgentOfficeExpenseSheetLines,
  buildTradeFeesDeductionRows,
  buildTradeOfficeExpenseSheetLines,
  getAgentOfficeExpenseAmounts,
  sumAgentOfficeExpenses,
  sumAgentOfficeExpensesByColumn,
} from "../utils/officeExpenses";
import {
  getBuyerRebateListingSellingForSheet,
  getNetToOfficeTotal,
  isBuyerRebateIncludedYes,
} from "../utils/tradeFinancialUtils";
import TradePaymentDetailsView from "./TradeDetailsModal/TradePaymentDetailsView";
import TradeNotesModal from "./TradeNotesModal";

/** One signature line per person on Commission Trust Agreement (ADE uses LISTING + SELLING rows). */
function dedupeAgentDoubleEnderForTrustSignatures(agents, tradeClassification) {
  if (!agents?.length) return agents || [];
  if (tradeClassification !== "AGENT DOUBLE ENDER") return agents;
  const out = [];
  const seen = new Set();
  for (const a of agents) {
    if ((a.classification || "").toUpperCase() !== "AGENT DOUBLE ENDER") {
      out.push(a);
      continue;
    }
    const k = String(a.agentId ?? a.agentName ?? "");
    if (seen.has(k)) continue;
    seen.add(k);
    const same = agents.filter(
      (x) =>
        (x.classification || "").toUpperCase() === "AGENT DOUBLE ENDER" &&
        String(x.agentId ?? x.agentName ?? "") === k,
    );
    const selling = same.find(
      (x) => (x.transactionType || "").toUpperCase() === "SELLING",
    );
    out.push(selling || same[0]);
  }
  return out;
}

const TradeDetailsModal = ({ trade, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);

  const [isPrinting, setIsPrinting] = useState(false);
  const [showPrintAgentModal, setShowPrintAgentModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [tradeNotes, setTradeNotes] = useState(trade?.notes || []);

  const tradeRecordSheetAgentPickerEntries = useMemo(() => {
    const list = (trade?.agentCommissionList || []).filter(
      (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
    );
    const isAde =
      (trade?.keyInfo?.classification || "") === "AGENT DOUBLE ENDER";
    if (!isAde) {
      return list.map((agent, idx) => ({
        key: `agent-${idx}`,
        label: agent.agentName || `Agent ${idx + 1}`,
        printIndex: idx,
      }));
    }
    const byKey = new Map();
    list.forEach((agent, idx) => {
      const kid = String(agent.agentId ?? agent.agentName ?? `idx-${idx}`);
      if (!byKey.has(kid)) byKey.set(kid, []);
      byKey.get(kid).push({ agent, idx });
    });
    return [...byKey.values()].map((pairs, groupIdx) => {
      const sellingPair = pairs.find(
        (p) => (p.agent.transactionType || "").toUpperCase() === "SELLING",
      );
      const primary = sellingPair || pairs[0];
      const { agent } = primary;
      return {
        key: `${agent.agentId ?? agent.agentName ?? groupIdx}`,
        label: agent.agentName || "Agent",
        printIndex: primary.idx,
      };
    });
  }, [trade?.agentCommissionList, trade?.keyInfo?.classification]);

  const tradeDetailsExtras = useMemo(() => {
    if (!trade) {
      return {
        feesDeductionRows: [],
        officeExpenseSheet: { lines: [], totals: {} },
        netToOffice: null,
      };
    }
    const isCoOp =
      (trade.keyInfo?.classification || "") === "CO-OPERATING SIDE";
    return {
      feesDeductionRows: buildTradeFeesDeductionRows(trade),
      officeExpenseSheet: buildTradeOfficeExpenseSheetLines(trade, {
        isCoOperatingSide: isCoOp,
      }),
      netToOffice: getNetToOfficeTotal(trade),
    };
  }, [trade]);

  if (!trade) return null;

  // Helper: get nested safely

  const get = (obj, path, fallback = "-") =>
    path

      .split(".")

      .reduce((o, k) => (o && o[k] !== undefined ? o[k] : fallback), obj);

  // Helper to render a table from an array of objects

  const renderTable = (rows) => {
    if (!rows || !rows.length)
      return <div className="text-gray-500">No data.</div>;

    const headers = Object.keys(rows[0]);

    const capitalizedHeaders = headers.map(
      (h) => h.charAt(0).toUpperCase() + h.slice(1)
    );

    return (
      <table className="min-w-full border border-gray-300 mb-2 text-sm">
        <thead>
          <tr>
            {headers.map((h, index) => (
              <th key={h} className="px-2 py-1 text-left bg-gray-100 border">
                {capitalizedHeaders[index]}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {headers.map((h) => (
                <td key={h} className="border px-2 py-1">
                  {row[h]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Add a helper function inside the component, before renderFinancialSection

  const displayAmount = (val) =>
    val !== undefined && val !== null && val !== "" ? val : "0.00";

  // Print function

  const handlePrint = async (selectedAgentIndex = null) => {
    if (isPrinting) return;

    setIsPrinting(true);

    try {
      let printWindow = window.open("", "_blank");

      let useCurrentWindow = false;

      if (!printWindow) {
        useCurrentWindow = true;

        printWindow = window;
      }

      const currentDate = new Date().toLocaleDateString();

      let realEstateTrustEFTs = [];

      let commissionTrustEFTs = [];

      let ledgerEntries = [];

      if (trade && trade._id) {
        try {
          const [realEstateRes, commissionRes, ledgerRes] = await Promise.all([
            axiosInstance.get(`/real-estate-trust-eft/trade/${trade._id}`),

            axiosInstance.get(`/commission-trust-eft/trade/${trade._id}`),

            axiosInstance.get("/ledger"),
          ]);

          realEstateTrustEFTs = realEstateRes.data || [];

          commissionTrustEFTs = commissionRes.data || [];

          const allLedgerEntries = ledgerRes.data || [];

          const tradeNumber = trade.tradeNumber || trade.keyInfo?.tradeNumber;

          ledgerEntries = allLedgerEntries.filter(
            (entry) =>
              entry.tradeNumber === tradeNumber ||
              (entry.description &&
                entry.description.includes(`Trade #: ${tradeNumber}`)),
          );
        } catch (err) {
          // fallback to empty arrays
        }
      }

      const commissionIncome =
        trade.commission?.commissionIncomeRows?.[0] || {};

      const tradeClassification = trade.keyInfo?.classification || "";

      const isCoOperatingSide = tradeClassification === "CO-OPERATING SIDE";

      const listingAmount =
        commissionIncome.listingAmount && commissionIncome.listingAmount !== ""
          ? parseFloat(commissionIncome.listingAmount)
          : 0;

      const sellingAmount =
        commissionIncome.sellingAmount && commissionIncome.sellingAmount !== ""
          ? parseFloat(commissionIncome.sellingAmount)
          : 0;

      const subTotal = (listingAmount + sellingAmount).toFixed(2);

      const taxRate = getTradeTaxRate(trade);

      const listingTax = roundTo2(listingAmount * taxRate).toFixed(2);

      const sellingTax = roundTo2(sellingAmount * taxRate).toFixed(2);

      const hst = roundTo2(
        parseFloat(listingTax) + parseFloat(sellingTax),
      ).toFixed(2);

      const total = roundTo2(parseFloat(subTotal) + parseFloat(hst)).toFixed(2);

      const outsideBrokersList = [];
      let totalObListing = 0;
      let totalObSelling = 0;
      let totalObSubTotal = 0;
      let totalObTax = 0;
      let totalObTotal = 0;

      const brokerRows =
        trade.commission?.outsideBrokersRows?.length > 0
          ? trade.commission.outsideBrokersRows
          : trade.outsideBrokers || [];

      brokerRows.forEach((outsideBrokerInfo) => {
        // Get the broker name
        const brokerName = `${
          outsideBrokerInfo.agentName || outsideBrokerInfo.firstName || ""
        } ${outsideBrokerInfo.lastName || ""}`.trim();

        // Get the company name
        const companyName =
          outsideBrokerInfo.company || outsideBrokerInfo.brokerage || "";

        // Combine name and company
        const obName =
          brokerName && companyName
            ? `${brokerName} - ${companyName}`
            : brokerName || companyName || "Outside Broker";

        // Get the selling amount from either source
        const brokerSellingAmount = outsideBrokerInfo.sellingAmount || "0.00";

        // First check the broker's end from outsideBrokers array
        const matchingBrokerInOB = trade.outsideBrokers?.find((ob) => {
          const outsideBrokerName = `${ob.firstName} ${ob.lastName}`.trim();
          return (
            outsideBrokerName ===
              (outsideBrokerInfo.agentName || outsideBrokerInfo.firstName) ||
            ob.company === outsideBrokerInfo.company
          );
        });

        // Determine the end type from either the matching broker or current broker info
        let endType = "";

        if (matchingBrokerInOB) {
          if (
            matchingBrokerInOB.end?.toLowerCase().includes("listing") ||
            matchingBrokerInOB.type?.toLowerCase().includes("listing")
          ) {
            endType = "listing";
          } else {
            endType = "selling";
          }
        } else {
          const end = (
            outsideBrokerInfo.end ||
            outsideBrokerInfo.type ||
            ""
          ).toLowerCase();
          endType = end.includes("listing") ? "listing" : "selling";
        }

        // Place amount in appropriate column based on end type
        let obListing = "0.00";
        let obSelling = "0.00";

        if (endType === "listing") {
          obListing = brokerSellingAmount;
          obSelling = "0.00";
        } else {
          obListing = "0.00";
          obSelling = brokerSellingAmount;
        }

        const obSubTotal = brokerSellingAmount;
        const obTax = outsideBrokerInfo.tax || "0.00";
        const obTotal = outsideBrokerInfo.total || "0.00";

        // Add to totals
        totalObListing += parseFloat(obListing);
        totalObSelling += parseFloat(obSelling);
        totalObSubTotal += parseFloat(obSubTotal);
        totalObTax += parseFloat(obTax);
        totalObTotal += parseFloat(obTotal);

        // Add to list
        outsideBrokersList.push({
          obName,
          obListing,
          obSelling,
          obSubTotal,
          obTax,
          obTotal,
        });
      });

      if (tradeClassification === "AGENT DOUBLE ENDER") {
        outsideBrokersList.length = 0;
        totalObListing = 0;
        totalObSelling = 0;
        totalObSubTotal = 0;
        totalObTax = 0;
        totalObTotal = 0;
      }

      const obListing = totalObListing.toFixed(2);
      const obSelling = totalObSelling.toFixed(2);
      const obSubTotal = totalObSubTotal.toFixed(2);
      const obTax = totalObTax.toFixed(2);
      const obTotal = totalObTotal.toFixed(2);
      const obName =
        outsideBrokersList.length > 0 ? outsideBrokersList[0].obName : "";

      let baseOfficeCommissionListing,
        baseOfficeCommissionSelling,
        baseOfficeCommissionSubTotal,
        baseOfficeCommissionHST,
        baseOfficeCommissionTotal;

      const referralForExpenses = (trade.agentCommissionList || []).filter(
        (a) => (a.classification || "").toUpperCase() === "REFERRAL",
      );
      const referralAmountSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.amount || 0),
        0,
      );
      const referralTaxSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.tax || 0),
        0,
      );
      const referralTotalSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.total || 0),
        0,
      );

      const expensesRowsForPrint = [];
      outsideBrokersList.forEach((b) => {
        expensesRowsForPrint.push({
          name: b.obName,
          listing: b.obListing,
          selling: b.obSelling,
          subTotal: b.obSubTotal,
          tax: b.obTax,
          total: b.obTotal,
        });
      });
      referralForExpenses.forEach((a) => {
        expensesRowsForPrint.push({
          name: `${a.agentName || ""} - Referral`,
          listing: "0.00",
          selling: a.amount || "0",
          subTotal: a.amount || "0",
          tax: a.tax || "0",
          total: a.total || "0",
        });
      });

      if (isCoOperatingSide) {
        if (referralForExpenses.length === 0) {
          baseOfficeCommissionListing = listingAmount;
          baseOfficeCommissionSelling = sellingAmount;
          baseOfficeCommissionSubTotal = subTotal;
          baseOfficeCommissionHST = hst;
          baseOfficeCommissionTotal = total;
        } else {
          baseOfficeCommissionListing = listingAmount;
          baseOfficeCommissionSelling = (
            parseFloat(sellingAmount) - referralAmountSum
          ).toFixed(2);
          baseOfficeCommissionSubTotal = (
            parseFloat(subTotal) - referralAmountSum
          ).toFixed(2);
          baseOfficeCommissionHST = (parseFloat(hst) - referralTaxSum).toFixed(
            2,
          );
          baseOfficeCommissionTotal = (
            parseFloat(total) - referralTotalSum
          ).toFixed(2);
        }
      } else {
        baseOfficeCommissionListing = (
          parseFloat(listingAmount) - parseFloat(obListing)
        ).toFixed(2);
        baseOfficeCommissionSelling = (
          parseFloat(sellingAmount) -
          parseFloat(obSelling) -
          referralAmountSum
        ).toFixed(2);
        baseOfficeCommissionSubTotal = (
          parseFloat(subTotal) -
          parseFloat(obSubTotal) -
          referralAmountSum
        ).toFixed(2);
        baseOfficeCommissionHST = (
          parseFloat(hst) -
          parseFloat(obTax) -
          referralTaxSum
        ).toFixed(2);
        baseOfficeCommissionTotal = (
          parseFloat(total) -
          parseFloat(obTotal) -
          referralTotalSum
        ).toFixed(2);
      }

      const agentDoubleEnderOrder = new Map();
      if (tradeClassification === "AGENT DOUBLE ENDER") {
        (trade.agentCommissionList || []).forEach((a, index) => {
          if (a.classification === "AGENT DOUBLE ENDER" && a.agentId) {
            const key = a.agentId;
            if (!agentDoubleEnderOrder.has(key)) {
              agentDoubleEnderOrder.set(key, []);
            }
            agentDoubleEnderOrder.get(key).push(index);
          }
        });
      }

      const agentCalculations = (trade.agentCommissionList || []).map(
        (agent, agentIndex) => {
          const agentAmount =
            agent.amount && agent.amount !== "" ? parseFloat(agent.amount) : 0;
          const agentFeesDeducted =
            agent.feesDeducted && agent.feesDeducted !== ""
              ? parseFloat(agent.feesDeducted)
              : 0;
          const buyerRebateAmount =
            isBuyerRebateIncludedYes(agent) && agent.buyerRebateAmount
              ? parseFloat(agent.buyerRebateAmount) || 0
              : 0;
          const shareAfterPlan = agentAmount - agentFeesDeducted;
          const displaySubForSheet = Math.max(
            0,
            shareAfterPlan - buyerRebateAmount,
          );
          const hstBaseForAgent = shareAfterPlan;

          let agentListing = "0.00";
          let agentSelling = "0.00";

          if (
            tradeClassification === "AGENT DOUBLE ENDER" &&
            agent.classification === "AGENT DOUBLE ENDER"
          ) {
            let transactionType = agent.transactionType;
            if (!transactionType && agent.agentId) {
              const agentIndices =
                agentDoubleEnderOrder.get(agent.agentId) || [];
              const positionInList = agentIndices.indexOf(agentIndex);
              transactionType = positionInList === 0 ? "LISTING" : "SELLING";
            }
            if (transactionType === "LISTING") {
              agentListing = displaySubForSheet.toFixed(2);
              agentSelling = "0.00";
            } else if (transactionType === "SELLING") {
              agentListing = "0.00";
              agentSelling = displaySubForSheet.toFixed(2);
            }
          } else if (isCoOperatingSide) {
            agentListing = "0.00";
            agentSelling = displaySubForSheet.toFixed(2);
          } else {
            agentListing = displaySubForSheet.toFixed(2);
            agentSelling = "0.00";
          }

          const agentSubTotal = (
            parseFloat(agentListing) + parseFloat(agentSelling)
          ).toFixed(2);
          const defaultAgentHST = roundTo2(hstBaseForAgent * taxRate);
          const isTaxOverridden =
            agent.taxOverridden === true || agent.taxOverridden === "true";
          const agentHST = (
            isTaxOverridden
              ? roundTo2(parseFloat(agent.tax || 0) || 0)
              : defaultAgentHST
          ).toFixed(2);
          const agentTotal = (
            parseFloat(agentSubTotal) + parseFloat(agentHST)
          ).toFixed(2);

          return {
            ...agent,
            agentListing,
            agentSelling,
            agentSubTotal,
            agentHST,
            agentTotal,
          };
        },
      );

      const showBaseOfficeAndNetToOffice =
        selectedAgentIndex === null || selectedAgentIndex === undefined;
      const nonReferralAgents = agentCalculations.filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );
      let agentCalculationsFiltered = agentCalculations;
      if (!showBaseOfficeAndNetToOffice && selectedAgentIndex != null) {
        const indices = Array.isArray(selectedAgentIndex)
          ? selectedAgentIndex
          : [selectedAgentIndex];
        const selected = indices
          .filter((i) => Number.isInteger(i) && nonReferralAgents[i])
          .map((i) => nonReferralAgents[i]);
        if (selected.length > 0) {
          agentCalculationsFiltered = selected;
        }
      }
      const agentsForNetToOffice = agentCalculationsFiltered.filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );

      const agentKeysForBuyerRebateSheet = showBaseOfficeAndNetToOffice
        ? null
        : new Set(
            agentsForNetToOffice.map((a) =>
              String(a.agentId ?? a.agentName ?? ""),
            ),
          );
      const buyerRebateCols = getBuyerRebateListingSellingForSheet(trade, {
        agentKeys: agentKeysForBuyerRebateSheet,
      });
      const buyerRebateListing = buyerRebateCols.listing.toFixed(2);
      const buyerRebateSelling = buyerRebateCols.selling.toFixed(2);
      const totalBuyerRebate = parseFloat(
        (buyerRebateCols.listing + buyerRebateCols.selling).toFixed(2),
      );

      let netToOfficeListing,
        netToOfficeSelling,
        netToOfficeSubTotal,
        netToOfficeHST,
        netToOfficeTotal;

      let totalAgentListing,
        totalAgentSelling,
        totalAgentSubTotal,
        totalAgentHST,
        totalAgentTotal;

      if (!isCoOperatingSide && agentsForNetToOffice.length === 2) {
        const agent1 = agentsForNetToOffice[0] || {};
        const agent2 = agentsForNetToOffice[1] || {};
        totalAgentListing =
          parseFloat(agent1.agentListing || 0) -
          parseFloat(agent2.agentListing || 0);
        totalAgentSelling =
          parseFloat(agent1.agentSelling || 0) -
          parseFloat(agent2.agentSelling || 0);
        totalAgentSubTotal =
          parseFloat(agent1.agentSubTotal || 0) -
          parseFloat(agent2.agentSubTotal || 0);
        totalAgentHST =
          parseFloat(agent1.agentHST || 0) - parseFloat(agent2.agentHST || 0);
        totalAgentTotal =
          parseFloat(agent1.agentTotal || 0) -
          parseFloat(agent2.agentTotal || 0);
      } else {
        totalAgentListing = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentListing || 0),
          0,
        );
        totalAgentSelling = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentSelling || 0),
          0,
        );
        totalAgentSubTotal = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentSubTotal || 0),
          0,
        );
        totalAgentHST = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentHST || 0),
          0,
        );
        totalAgentTotal = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentTotal || 0),
          0,
        );
      }

      if (tradeClassification === "AGENT DOUBLE ENDER") {
        const sumAgentListing = agentsForNetToOffice.reduce(
          (s, a) => s + parseFloat(a.agentListing || 0),
          0,
        );
        const sumAgentSelling = agentsForNetToOffice.reduce(
          (s, a) => s + parseFloat(a.agentSelling || 0),
          0,
        );
        const sumAgentSubTotal = agentsForNetToOffice.reduce(
          (s, a) => s + parseFloat(a.agentSubTotal || 0),
          0,
        );
        const sumAgentHST = agentsForNetToOffice.reduce(
          (s, a) => s + parseFloat(a.agentHST || 0),
          0,
        );
        const sumAgentTotal = agentsForNetToOffice.reduce(
          (s, a) => s + parseFloat(a.agentTotal || 0),
          0,
        );
        const incomeSub = parseFloat(subTotal);
        const incomeHst = parseFloat(hst);
        const incomeTtl = parseFloat(total);
        const officeExpTotals = sumAgentOfficeExpenses(agentsForNetToOffice);
        const officeExpCols = sumAgentOfficeExpensesByColumn(
          agentsForNetToOffice,
          isCoOperatingSide,
        );
        netToOfficeListing = (
          parseFloat(listingAmount) -
          sumAgentListing -
          officeExpCols.listing
        ).toFixed(2);
        netToOfficeSelling = (
          parseFloat(sellingAmount) -
          sumAgentSelling -
          officeExpCols.selling
        ).toFixed(2);
        netToOfficeSubTotal = (
          incomeSub - sumAgentSubTotal - officeExpTotals.subTotal
        ).toFixed(2);
        netToOfficeHST = (
          incomeHst - sumAgentHST - officeExpTotals.taxTotal
        ).toFixed(2);
        netToOfficeTotal = (
          incomeTtl - sumAgentTotal - officeExpTotals.total
        ).toFixed(2);
      } else if (!isCoOperatingSide && agentsForNetToOffice.length === 2) {
        const totalPlanFees = agentsForNetToOffice.reduce((tot, agent) => {
          const officeExp = getAgentOfficeExpenseAmounts(agent);
          return (
            tot +
            parseFloat(agent.feesDeducted || 0) +
            officeExp.subTotal
          );
        }, 0);
        const officeExpenseTaxTotal = agentsForNetToOffice.reduce(
          (tot, agent) => tot + getAgentOfficeExpenseAmounts(agent).taxTotal,
          0,
        );
        const overriddenHSTToOffice = agentsForNetToOffice.reduce(
          (tot, agent) => {
            const rowOverride =
              agent.taxOverridden === true || agent.taxOverridden === "true";
            return (
              tot +
              (rowOverride
                ? roundTo2(parseFloat(agent.amount || 0) * taxRate)
                : 0)
            );
          },
          0,
        );
        netToOfficeSubTotal = totalPlanFees.toFixed(2);
        netToOfficeHST = roundTo2(
          overriddenHSTToOffice + officeExpenseTaxTotal,
        ).toFixed(2);
        netToOfficeTotal = roundTo2(
          totalPlanFees + overriddenHSTToOffice + officeExpenseTaxTotal,
        ).toFixed(2);
        netToOfficeListing = netToOfficeSubTotal;
        netToOfficeSelling = "0.00";
      } else {
        const officeExpTotals = sumAgentOfficeExpenses(agentsForNetToOffice);
        const officeExpCols = sumAgentOfficeExpensesByColumn(
          agentsForNetToOffice,
          isCoOperatingSide,
        );
        netToOfficeListing = (
          parseFloat(baseOfficeCommissionListing) -
          totalAgentListing -
          officeExpCols.listing -
          buyerRebateCols.listing
        ).toFixed(2);
        netToOfficeSelling = (
          parseFloat(baseOfficeCommissionSelling) -
          totalAgentSelling -
          officeExpCols.selling -
          buyerRebateCols.selling
        ).toFixed(2);
        netToOfficeSubTotal = (
          parseFloat(baseOfficeCommissionSubTotal) -
          totalAgentSubTotal -
          officeExpTotals.subTotal -
          totalBuyerRebate
        ).toFixed(2);
        netToOfficeHST = (
          parseFloat(baseOfficeCommissionHST) -
          totalAgentHST -
          officeExpTotals.taxTotal
        ).toFixed(2);
        netToOfficeTotal = (
          parseFloat(baseOfficeCommissionTotal) -
          totalAgentTotal -
          officeExpTotals.total -
          totalBuyerRebate
        ).toFixed(2);
      }

      const getEndValueForPrintBroker = (b) => {
        const matchingBroker1 = trade.outsideBrokers?.find((ob) => {
          const outsideBrokerName = `${ob.firstName} ${ob.lastName}`.trim();
          return (
            outsideBrokerName === b.agentName || ob.company === b.brokerage
          );
        });
        if (matchingBroker1) {
          if (
            matchingBroker1.end?.toLowerCase().includes("listing") ||
            matchingBroker1.type?.toLowerCase().includes("listing")
          )
            return "Listing";
        }
        if (
          b.end?.toLowerCase().includes("listing") ||
          b.type?.toLowerCase().includes("listing")
        )
          return "Listing";
        return "Selling";
      };

      const formattedOutsideBrokersBase =
        trade.commission?.outsideBrokersRows?.map((broker) => ({
          agentName: broker.agentName || "-",
          brokerage: broker.brokerage || "-",
          end: getEndValueForPrintBroker(broker),
          sellingAmount: broker.sellingAmount || "0.00",
          tax: broker.tax || "0.00",
          total: broker.total || "0.00",
        })) || [];

      const referralAgentsForPrint = (trade.agentCommissionList || []).filter(
        (a) => (a.classification || "").toUpperCase() === "REFERRAL",
      );
      const referralCommissionRowsForPrint = referralAgentsForPrint.map(
        (a) => ({
          agentName: a.agentName || "-",
          brokerage: a.agentName || "Referral",
          end: "Selling",
          sellingAmount: a.amount || "0.00",
          tax: a.tax || "0.00",
          total: a.total || "0.00",
        }),
      );
      const formattedOutsideBrokers = [
        ...formattedOutsideBrokersBase,
        ...referralCommissionRowsForPrint,
      ];

      const referralAsBrokersForPrint = referralAgentsForPrint.map((a) => ({
        type: "Referral",
        firstName: "-",
        lastName: "-",
        company: a.agentName || "Referral",
        end: "Selling End",
        primaryPhone: "-",
      }));
      const allOutsideBrokersForPrint = [
        ...(trade.outsideBrokers || []),
        ...referralAsBrokersForPrint,
      ];

      const agentDetailsForPrint = dedupeAgentDoubleEnderForTrustSignatures(
        (agentCalculationsFiltered || []).filter(
          (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
        ),
        tradeClassification,
      );
      const financialAgentRowsForPrint = (
        agentCalculationsFiltered || []
      ).filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );

      // Get address information from keyInfo

      const streetNumber = get(trade, "keyInfo.streetNumber") || "";

      const streetName = get(trade, "keyInfo.streetName") || "";

      const unit = get(trade, "keyInfo.unit") || "";

      const city = get(trade, "keyInfo.city") || "";

      const province = get(trade, "keyInfo.province") || "";

      const postalCode = get(trade, "keyInfo.postalCode") || "";

      // Format address for display

      const addressLine1 =
        streetNumber && streetName ? `${streetNumber} ${streetName}` : "";

      const addressLine2 = unit || "";

      const addressLine3 = city && province ? `${city}, ${province}` : "";

      const addressLine4 = postalCode || "";

      const finDataRow = (label, values, options = {}) => {
        const { indent = false, bold = false, netRow = false } = options;
        const labelClass = indent ? "fin-label-indent" : "fin-label";
        const labelStyle = bold ? ' style="font-weight:bold;"' : "";
        const rowClass = netRow ? ' class="fin-net-row"' : "";
        const [listing, selling, subTotal, rowHst, rowTotal] = values;
        return `<tr${rowClass}>
    <td class="${labelClass}"${labelStyle}>${label}</td>
    <td class="fin-num">${formatCurrency(listing)}</td>
    <td class="fin-num">${formatCurrency(selling)}</td>
    <td class="fin-num">${formatCurrency(subTotal)}</td>
    <td class="fin-num">${formatCurrency(rowHst)}</td>
    <td class="fin-num">${formatCurrency(rowTotal)}</td>
  </tr>`;
      };

      const finSectionRow = (title, options = {}) => {
        const { divider = false, netSection = false } = options;
        let rowClass = "fin-section";
        if (netSection) rowClass += " fin-section-net";
        else if (divider) rowClass += " fin-section-divider";
        return `<tr class="${rowClass}"><td colspan="6">${title}</td></tr>`;
      };

      const finSheetBodyRows = [];
      finSheetBodyRows.push(finSectionRow("Income"));
      finSheetBodyRows.push(
        finDataRow("Commission", [
          listingAmount,
          sellingAmount,
          subTotal,
          hst,
          total,
        ]),
      );

      if (showBaseOfficeAndNetToOffice) {
        finSheetBodyRows.push(
          finDataRow("Base Office Commission", [
            baseOfficeCommissionListing,
            baseOfficeCommissionSelling,
            baseOfficeCommissionSubTotal,
            baseOfficeCommissionHST,
            baseOfficeCommissionTotal,
          ]),
        );
      }

      if (expensesRowsForPrint.length > 0) {
        finSheetBodyRows.push(finSectionRow("Expenses", { divider: true }));
        expensesRowsForPrint.forEach((row) => {
          const isReferral = (row.name || "").includes("Referral");
          const label = isReferral
            ? `Ref. ${(row.name || "").replace(/\s*-\s*Referral\s*$/i, "")}`
            : row.name;
          finSheetBodyRows.push(
            finDataRow(label, [
              row.listing,
              row.selling,
              row.subTotal,
              row.tax,
              row.total,
            ]),
          );
        });
      }

      finSheetBodyRows.push(finSectionRow("Agents", { divider: true }));
      financialAgentRowsForPrint.forEach((agent) => {
        finSheetBodyRows.push(
          finDataRow(`(${agent.agentId || ""}) ${agent.agentName || ""}`, [
            agent.agentListing || "0.00",
            agent.agentSelling || "0.00",
            agent.agentSubTotal || "0.00",
            agent.agentHST || "0.00",
            agent.agentTotal || "0.00",
          ]),
        );
        buildAgentOfficeExpenseSheetLines(agent, isCoOperatingSide).forEach(
          (line) => {
            finSheetBodyRows.push(
              finDataRow(
                line.label,
                [line.listing, line.selling, line.subTotal, line.hst, line.total],
                { indent: true },
              ),
            );
          },
        );
      });

      if (totalBuyerRebate > 0) {
        finSheetBodyRows.push(finSectionRow("Liabilities", { divider: true }));
        const rebateSubTotal = (
          parseFloat(buyerRebateListing) + parseFloat(buyerRebateSelling)
        ).toFixed(2);
        finSheetBodyRows.push(
          finDataRow("Buyer Rebate", [
            buyerRebateListing,
            buyerRebateSelling,
            rebateSubTotal,
            "0.00",
            rebateSubTotal,
          ]),
        );
      }

      if (showBaseOfficeAndNetToOffice) {
        finSheetBodyRows.push(finSectionRow("Net to Office", { netSection: true }));
        finSheetBodyRows.push(
          finDataRow(
            "",
            [
              netToOfficeListing,
              netToOfficeSelling,
              netToOfficeSubTotal,
              netToOfficeHST,
              netToOfficeTotal,
            ],
            { netRow: true },
          ),
        );
      }

      const printContent = `

      <!DOCTYPE html>

      <html>

      <head>

        <title>Trade Record Sheet - ${trade.tradeNumber}</title>

        <style>

          body { font-family: Arial, sans-serif; margin: 15px; font-size: 11px; }

          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }

          .logo { width: 80px; height: auto; }

          .title { font-size: 18px; font-weight: bold; text-align: center; flex-grow: 1; }

          .date-address { text-align: right; font-size: 10px; }

          .date { font-weight: bold; margin-bottom: 5px; }

          .address { line-height: 1.2; }

          .section { margin-bottom: 15px; }

          .section-title { font-size: 14px; font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #333; padding-bottom: 3px; }

          .property-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px; }

          .property-item { display: flex; justify-content: space-between; font-size: 10px; }

          .property-label { font-weight: bold; }

          table { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 9px; }

          th, td { border: 1px solid #ddd; padding: 4px; text-align: left; }

          th { background-color: #f2f2f2; font-weight: bold; }

          .financial-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }

          .financial-item { text-align: center; }

          .financial-label { font-weight: bold; margin-bottom: 3px; font-size: 10px; }

          .financial-value { font-size: 14px; }

          .fin-sheet { width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 9px; table-layout: fixed; border-bottom: 2px solid #000; }

          .fin-sheet th, .fin-sheet td { border: none; padding: 3px 4px; vertical-align: top; }

          .fin-sheet thead th { font-weight: bold; border-top: 1px solid #999; border-bottom: 1px solid #999; }

          .fin-meta-block { border-top: 2px solid #000; padding-top: 6px; margin-top: 4px; }

          .fin-meta-title { font-weight: bold; font-size: 12px; margin-bottom: 4px; }

          .fin-label { text-align: left; width: 28%; }

          .fin-label-indent { text-align: left; width: 28%; padding-left: 16px; }

          .fin-num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }

          .fin-section td { font-weight: bold; font-size: 10px; padding-top: 6px; padding-bottom: 0; }

          .fin-section-divider td { border-top: 1px solid #999; }

          .fin-section-net td { border-top: 2px solid #000; padding-top: 4px; }

          .fin-net-row td { border-bottom: 3px double #000; }

          @media print {

            body { margin: 0; }

            .no-print { display: none !important; }

            button { display: none !important; }

            .no-print button { display: none !important; }

          }

        </style>

      </head>

      <body>

        <div class="header">

          <div style="display: flex; flex-direction: column; align-items: flex-start;">

            <img src="${logo1}" alt="Logo" class="logo">

            <div style="font-size: 9px; line-height: 1.2; margin-top: 5px;">

                      Bestway Real Estate Ltd., Brokerage<br>

      500 St.George Street, Moncton, NB E1C 1Y3<br>

        Phone: 506-268-2900

            </div>

          </div>

          <div class="title">Trade Record Sheet</div>

          <div class="date-address">

            <div class="date">${currentDate}</div>

            <div class="address">

              ${addressLine1}<br>

              ${addressLine2}<br>

              ${addressLine3}<br>

              ${addressLine4}

            </div>

            <div style="font-size: 10px; font-weight: bold; margin-top: 5px;">

              Trade Number - ${
                get(trade, "keyInfo.tradeNumber") || trade.tradeNumber || "-"
              }

            </div>

          </div>

        </div>



        <div class="section">

          <div class="section-title">Property</div>

          <div class="property-grid">

            <div class="property-item">

              <span class="property-label">Type:</span>

              <span>${get(trade, "keyInfo.propertyType")}</span>

            </div>

            <div class="property-item">

              <span class="property-label">Classification:</span>

              <span>${get(trade, "keyInfo.classification")}</span>

            </div>

            <div class="property-item">

              <span class="property-label">Status:</span>

              <span>${get(trade, "keyInfo.status")}</span>

            </div>

            <div class="property-item">

              <span class="property-label">MLS Number:</span>

              <span>${get(trade, "keyInfo.mlsNumber")}</span>

            </div>

            <div class="property-item">

              <span class="property-label">Offer Date:</span>

              <span>${get(trade, "keyInfo.offerDate")}</span>

            </div>

            <div class="property-item">

              <span class="property-label">Entry Date:</span>

              <span>${get(trade, "keyInfo.entryDate")}</span>

            </div>

            <div class="property-item">

              <span class="property-label">Firm Date:</span>

              <span>${get(trade, "keyInfo.firmDate")}</span>

            </div>

            <div class="property-item">

              <span class="property-label">Close Date:</span>

              <span>${get(trade, "keyInfo.closeDate")}</span>

            </div>

          </div>

        </div>



        <div class="section">

          <div class="section-title">Conditions</div>

          ${
            trade.conditions && trade.conditions.length > 0
              ? `

          <table>

            <thead>

              <tr>

                <th>Condition</th>

                <th>Due Date</th>

                <th>Condition Met Date</th>

              </tr>

            </thead>

            <tbody>

              ${trade.conditions

                .map(
                  (condition) => `

                <tr>

                  <td>${condition.conditionText || "-"}</td>

                  <td>${condition.dueDate || "-"}</td>

                  <td>${condition.conditionMetDate || "Not met"}</td>

                </tr>

              `
                )

                .join("")}

            </tbody>

          </table>

          `
              : "<p>No conditions available.</p>"
          }

        </div>



        <div class="section">

          <div class="section-title">Contacts</div>

          ${
            trade.people && trade.people.length > 0
              ? `

          <table>

            <thead>

              <tr>

                <th>Type</th>

                <th>First Name</th>

                <th>Last Name</th>

                <th>Primary Phone</th>

                <th>Secondary Phone</th>

                <th>Email</th>

                <th>Company Name</th>

              </tr>

            </thead>

            <tbody>

              ${trade.people

                .map(
                  (person) => `

                <tr>

                  <td>${person.type || "-"}</td>

                  <td>${person.firstName || "-"}</td>

                  <td>${person.lastName || "-"}</td>

                  <td>${person.primaryPhone || "-"}</td>

                  <td>${person.secondaryPhone || "-"}</td>

                  <td>${person.email || "-"}</td>

                  <td>${person.companyName || "-"}</td>

                </tr>

              `
                )

                .join("")}

            </tbody>

          </table>

          `
              : "<p>No contacts available.</p>"
          }

        </div>



        <div class="section">

          <div class="section-title">Outside Brokers</div>

          ${
            allOutsideBrokersForPrint && allOutsideBrokersForPrint.length > 0
              ? `

          <table>

            <thead>

              <tr>

                <th>Type</th>

                <th>End</th>

                <th>First Name</th>

                <th>Last Name</th>

                <th>Company</th>

                <th>Phone Number</th>

              </tr>

            </thead>

            <tbody>

              ${allOutsideBrokersForPrint

                .map(
                  (broker) => `

                <tr>

                  <td>${broker.type || "-"}</td>

                  <td>${broker.end || "-"}</td>

                  <td>${broker.firstName || "-"}</td>

                  <td>${broker.lastName || "-"}</td>

                  <td>${broker.company || "-"}</td>

                  <td>${broker.primaryPhone || "-"}</td>

                </tr>

              `,
                )

                .join("")}

            </tbody>

          </table>

          `
              : "<p>No outside brokers available.</p>"
          }

        </div>



        <div class="section">

          <div class="section-title">Outside Brokers Commission Details</div>

          ${
            formattedOutsideBrokers.length > 0
              ? `

          <table>

            <thead>

              <tr>

                <th>Agent Name</th>

                <th>Brokerage</th>

                <th>End</th>

                <th>Selling Amount</th>

                <th>Tax</th>

                <th>Total</th>

              </tr>

            </thead>

            <tbody>

              ${formattedOutsideBrokers

                .map(
                  (broker) => `

                <tr>

                  <td>${broker.agentName}</td>

                  <td>${broker.brokerage}</td>

                  <td>${broker.end}</td>

                  <td>${formatCurrency(broker.sellingAmount)}</td>

                  <td>${formatCurrency(broker.tax)}</td>

                  <td>${formatCurrency(broker.total)}</td>

                </tr>

              `
                )

                .join("")}

            </tbody>

          </table>

          `
              : "<p>No outside brokers commission details available.</p>"
          }

        </div>



        <div class="section">

          <div class="section-title">Deposit</div>

          ${
            trade.trustRecords && trade.trustRecords.length > 0
              ? `

          <table>

            <thead>

              <tr>

                <th>We Hold</th>

                <th>Received</th>

                <th>Received From</th>

                <th>Held By</th>

                <th>Deposit Date</th>

                <th>Reference</th>

                <th>Amount</th>

                <th>Payment Type</th>

              </tr>

            </thead>

            <tbody>

              ${trade.trustRecords

                .map(
                  (record) => `

                <tr>

                  <td>${record.weHold || "-"}</td>

                  <td>${record.received || "-"}</td>

                  <td>${record.receivedFrom || "-"}</td>

                  <td>${record.heldBy || "-"}</td>

                  <td>${record.depositDate || record.date || "-"}</td>

                  <td>${record.reference || "-"}</td>

                  <td>${formatCurrency(record.amount || 0)}</td>

                  <td>${record.paymentType || "-"}</td>

                </tr>

              `
                )

                .join("")}

            </tbody>

          </table>

          `
              : "<p>No deposit records available.</p>"
          }

        </div>



        <div class="section">

          <div class="section-title">Financial</div>

          <div class="fin-meta-block">

            <div class="fin-meta-title">Financial:</div>

            <div style="margin-bottom:4px; font-size:10px;">

              <div>Selling Price: <span style="font-weight:bold;">${formatCurrency(
                commissionIncome.sellPrice || trade.keyInfo?.sellPrice || 0
              )}</span></div>

            </div>

            <div style="margin-bottom:4px; font-size:10px;">

              <div>Listing Commission: <span style="font-weight:bold;">${parseFloat(
                trade.keyInfo?.listCommission || 0
              )}%</span></div>

            </div>

            <div style="margin-bottom:4px; font-size:10px;">

              <div>Selling Commission: <span style="font-weight:bold;">${parseFloat(
                trade.keyInfo?.sellCommission || 0
              )}%</span></div>

            </div>

          </div>

          <table class="fin-sheet">

            <colgroup>

              <col style="width:28%">

              <col style="width:14.4%">

              <col style="width:14.4%">

              <col style="width:14.4%">

              <col style="width:14.4%">

              <col style="width:14.4%">

            </colgroup>

            <thead>

              <tr>

                <th class="fin-label"></th>

                <th class="fin-num">Listing</th>

                <th class="fin-num">Selling</th>

                <th class="fin-num">Sub-Total</th>

                <th class="fin-num">HST</th>

                <th class="fin-num">Total</th>

              </tr>

            </thead>

            <tbody>

              ${finSheetBodyRows.join("")}

            </tbody>

          </table>

        </div>



        <div class="section">

          <div class="section-title">Agent Details</div>

          ${
            tradeClassification !== "AGENT DOUBLE ENDER"
              ? `
          ${
            agentDetailsForPrint && agentDetailsForPrint.length > 0
              ? `
          <table style="width:100%; border-collapse:collapse; margin-bottom:10px;">

            <thead>

              <tr style="background-color:#f2f2f2;">

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Name</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Number</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Payment Plan</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Base</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Plan Amount</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Office Exp.</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Buyer Rebate</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Total</th>

              </tr>

            </thead>

            <tbody>

              ${agentDetailsForPrint
                .map((agent) => {
                  const officeExp = parseFloat(agent.officeExpensesTotal || 0);
                  const buyerRebate =
                    isBuyerRebateIncludedYes(agent) && agent.buyerRebateAmount
                      ? parseFloat(agent.buyerRebateAmount || 0)
                      : 0;
                  const netTotal =
                    agent.netCommission != null && agent.netCommission !== ""
                      ? parseFloat(agent.netCommission)
                      : parseFloat(agent.amount || 0) +
                        parseFloat(agent.tax || 0) -
                        parseFloat(agent.totalFees || 0) -
                        buyerRebate;
                  return `
                <tr>
                  <td style="border:1px solid #ddd; padding:4px;">${agent.agentName || "-"}</td>
                  <td style="border:1px solid #ddd; padding:4px;">${agent.agentId || "-"}</td>
                  <td style="border:1px solid #ddd; padding:4px;">${getPaymentPlanDisplayName(agent.feeInfo) || "-"}</td>
                  <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(parseFloat(agent.amount || 0))}</td>
                  <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(agent.feesDeducted || 0)}</td>
                  <td style="border:1px solid #ddd; padding:4px;">${
                    officeExp > 0 ? formatCurrency(officeExp) : "-"
                  }</td>
                  <td style="border:1px solid #ddd; padding:4px;">${
                    buyerRebate > 0 ? formatCurrency(buyerRebate) : "-"
                  }</td>
                  <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(netTotal)}</td>
                </tr>
              `;
                })
                .join("")}

            </tbody>

          </table>
          `
              : "<p>No agent payment details available.</p>"
          }
          `
              : `
          ${
            agentDetailsForPrint && agentDetailsForPrint.length > 0
              ? `
          <table style="width:100%; border-collapse:collapse; margin-bottom:10px;">

            <thead>

              <tr style="background-color:#f2f2f2;">

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Name</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Number</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Payment Plan</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Base</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Plan Amount</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Office Exp.</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Buyer Rebate</th>

                <th style="border:1px solid #ddd; padding:4px; text-align:left;">Total</th>

              </tr>

            </thead>

            <tbody>

              ${agentDetailsForPrint
                .filter((agent) => agent.classification === "AGENT DOUBLE ENDER")
                .map((agent) => {
                  const officeExp = parseFloat(agent.officeExpensesTotal || 0);
                  const buyerRebate =
                    isBuyerRebateIncludedYes(agent) && agent.buyerRebateAmount
                      ? parseFloat(agent.buyerRebateAmount || 0)
                      : 0;
                  const netTotal =
                    agent.netCommission != null && agent.netCommission !== ""
                      ? parseFloat(agent.netCommission)
                      : parseFloat(agent.amount || 0) +
                        parseFloat(agent.tax || 0) -
                        parseFloat(agent.totalFees || 0) -
                        buyerRebate;
                  return `
                <tr>
                  <td style="border:1px solid #ddd; padding:4px;">${agent.agentName || "-"}</td>
                  <td style="border:1px solid #ddd; padding:4px;">${agent.agentId || "-"}</td>
                  <td style="border:1px solid #ddd; padding:4px;">${getPaymentPlanDisplayName(agent.feeInfo) || "-"}</td>
                  <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(parseFloat(agent.amount || 0))}</td>
                  <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(agent.feesDeducted || 0)}</td>
                  <td style="border:1px solid #ddd; padding:4px;">${
                    officeExp > 0 ? formatCurrency(officeExp) : "-"
                  }</td>
                  <td style="border:1px solid #ddd; padding:4px;">${
                    buyerRebate > 0 ? formatCurrency(buyerRebate) : "-"
                  }</td>
                  <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(netTotal)}</td>
                </tr>
              `;
                })
                .join("")}

            </tbody>

          </table>
          `
              : "<p>No agent payment details available.</p>"
          }
          `
          }

        </div>



        <div class="section">

          <div class="section-title">Trust Activity</div>

          ${
            trade.trustRecords && trade.trustRecords.length > 0
              ? `<table><thead><tr>

                  <th>Received From</th><th>Deposit Date</th><th>Reference</th><th>Amount</th>

                </tr></thead><tbody>

                ${trade.trustRecords

                  .map(
                    (record) => `<tr>

                      <td>${record.receivedFrom || "-"}</td>

                      <td>${record.depositDate || record.date || "-"}</td>

                      <td>${record.reference || "-"}</td>

                      <td>${formatCurrency(record.amount || 0)}</td>

                    </tr>`
                  )

                  .join("")}

                </tbody></table>`
              : `<div class="text-gray-500">No trust records available.</div>`
          }

        </div>



        <div class="section">

          <div class="section-title">Real Estate Trust Payments</div>

          ${
            realEstateTrustEFTs.length > 0
              ? `<table><thead><tr>

                  <th>EFT #</th><th>Date</th><th>Amount</th><th>Recipient</th><th>Type</th><th>Description</th>

                </tr></thead><tbody>

                ${realEstateTrustEFTs

                  .map(
                    (eft) => `<tr>

                      <td>${eft.eftNumber || ""}</td>

                      <td>${
                        eft.chequeDate || eft.date
                          ? formatDate(eft.chequeDate || eft.date)
                          : "-"
                      }</td>

                      <td>${formatCurrency(eft.amount || "")}</td>

                      <td>${eft.recipient || ""}</td>

                      <td>${getEFTTypeDisplayName(eft.type) || ""}</td>

                      <td>${eft.description || ""}</td>

                    </tr>`
                  )

                  .join("")}

                </tbody></table>`
              : `<div class="text-gray-500">No Real Estate Trust Payments found for this trade.</div>`
          }

        </div>



        <div class="section">

          <div class="section-title">Commission Trust Activity</div>

          ${(() => {
            // For Listing Side and Agent Double Ender, include CommissionTransfer from Real Estate Trust Payments as first transaction

            // For Co-Operating Side, include CommissionTransfer with "Commission Received" description as first transaction

            let displayCommissionTrustEFTs = [...commissionTrustEFTs];

            // Normalize classification for comparison (case-insensitive)

            const normalizedClassification = (tradeClassification || "")

              .toUpperCase()

              .trim();

            if (
              normalizedClassification === "LISTING SIDE" ||
              normalizedClassification === "AGENT DOUBLE ENDER" ||
              normalizedClassification === "AGENT DOUBLLE ENDER"
            ) {
              const commissionTransferEFT = realEstateTrustEFTs.find(
                (eft) => eft.type === "CommissionTransfer"
              );

              if (commissionTransferEFT) {
                displayCommissionTrustEFTs = [
                  commissionTransferEFT,

                  ...commissionTrustEFTs,
                ];
              }

              // Check if trust amount is less than total commission and add "Received From" transaction as second item

              const trustRecords = trade.trustRecords || [];

              const commissionIncome =
                trade.commission?.commissionIncomeRows?.[0] || {};

              const listingAmount = commissionIncome.listingAmount
                ? parseFloat(commissionIncome.listingAmount)
                : 0;

              const sellingAmount = commissionIncome.sellingAmount
                ? parseFloat(commissionIncome.sellingAmount)
                : 0;

              const taxRateForTrust = getTradeTaxRate(trade);

              const listingTaxTrust = roundTo2(
                listingAmount * taxRateForTrust,
              );

              const sellingTaxTrust = roundTo2(
                sellingAmount * taxRateForTrust,
              );

              const totalCommission = roundTo2(
                listingAmount +
                  sellingAmount +
                  listingTaxTrust +
                  sellingTaxTrust,
              );

              const trustAmount = trustRecords.reduce((sum, record) => {
                const amount = parseFloat(
                  record.amount || record.depositAmount || record.total || 0
                );

                return sum + amount;
              }, 0);

              if (trustAmount < totalCommission) {
                let receivedFromLedger = null;

                if (ledgerEntries && ledgerEntries.length > 0) {
                  const commissionTransferEFTNumber =
                    commissionTransferEFT?.eftNumber?.toString() || "";

                  const candidateEntries = ledgerEntries.filter(
                    (entry) =>
                      entry.accountNumber === "10004" &&
                      entry.debit > 0 &&
                      entry.description &&
                      (entry.description.includes("Received From:") ||
                        entry.description.includes("Received from:")) &&
                      // Exclude if it matches the CommissionTransfer EFT number

                      entry.eftNumber?.toString() !==
                        commissionTransferEFTNumber &&
                      // Exclude entries that have "Commission Trust" in description (those are from CommissionTransfer)

                      !entry.description.includes("Commission Trust") &&
                      // Exclude entries that have "Commission Trust" anywhere (to be safe)

                      !entry.description

                        .toLowerCase()

                        .includes("commission trust")
                  );

                  // If we have candidates, prefer the one that matches the pattern from final transactions

                  // (format: "Trade #: X - Received From: Name" or "Trade #: X - Address - Received From: Name")

                  if (candidateEntries.length > 0) {
                    // Find the one that matches the final transaction pattern (has " - Received From:" with dash before it)

                    receivedFromLedger =
                      candidateEntries.find((entry) =>
                        entry.description.includes(" - Received From:")
                      ) || candidateEntries[0];
                  }
                }

                if (receivedFromLedger) {
                  // Extract recipient from description

                  let recipient = receivedFromLedger.payee || "";

                  if (!recipient && receivedFromLedger.description) {
                    const receivedFromMatch =
                      receivedFromLedger.description.match(
                        /Received From:\s*([^-]+?)(?:\s*-|$)/i
                      );

                    if (receivedFromMatch) {
                      recipient = receivedFromMatch[1].trim();
                    }
                  }

                  // Create the balance commission payment transaction from ledger entry

                  const balanceCommissionPayment = {
                    eftNumber:
                      receivedFromLedger.eftNumber ||
                      receivedFromLedger.reference?.replace("EFT#", "") ||
                      "",

                    date:
                      receivedFromLedger.date || receivedFromLedger.chequeDate,

                    chequeDate:
                      receivedFromLedger.chequeDate || receivedFromLedger.date,

                    amount:
                      receivedFromLedger.debit || receivedFromLedger.amount,

                    recipient: recipient || "N/A",

                    type: "BalanceCommissionPayment",

                    description: "Balance Amount Received",

                    _id: `balance-${receivedFromLedger._id || Date.now()}`,
                  };

                  // Insert as second transaction (after CommissionTransfer)

                  if (commissionTransferEFT) {
                    displayCommissionTrustEFTs = [
                      commissionTransferEFT,

                      balanceCommissionPayment,

                      ...commissionTrustEFTs,
                    ];
                  } else {
                    displayCommissionTrustEFTs = [
                      balanceCommissionPayment,

                      ...commissionTrustEFTs,
                    ];
                  }
                } else {
                  // Fallback: Find the "Received From" transaction from trust records

                  const receivedFromRecord = trustRecords.find(
                    (record) =>
                      record.receivedFrom && record.paymentType === "EFT"
                  );

                  if (receivedFromRecord) {
                    // Extract EFT number from reference or use a default

                    const eftNumber =
                      receivedFromRecord.reference?.replace("EFT#", "") ||
                      receivedFromRecord.eftNumber ||
                      "";

                    // Create the balance commission payment transaction

                    const balanceCommissionPayment = {
                      eftNumber: eftNumber,

                      date:
                        receivedFromRecord.depositDate ||
                        receivedFromRecord.date,

                      chequeDate:
                        receivedFromRecord.depositDate ||
                        receivedFromRecord.date,

                      amount: parseFloat(
                        receivedFromRecord.amount ||
                          receivedFromRecord.depositAmount ||
                          receivedFromRecord.total ||
                          0
                      ),

                      recipient: receivedFromRecord.receivedFrom || "",

                      type: "BalanceCommissionPayment",

                      description: "Balance Amount Received",

                      _id: `balance-${receivedFromRecord._id || Date.now()}`,
                    };

                    // Insert as second transaction (after CommissionTransfer)

                    if (commissionTransferEFT) {
                      displayCommissionTrustEFTs = [
                        commissionTransferEFT,

                        balanceCommissionPayment,

                        ...commissionTrustEFTs,
                      ];
                    } else {
                      displayCommissionTrustEFTs = [
                        balanceCommissionPayment,

                        ...commissionTrustEFTs,
                      ];
                    }
                  }
                }
              }
            } else if (normalizedClassification === "CO-OPERATING SIDE") {
              // First, try to find CommissionTransfer in Real Estate Trust Payments

              let commissionReceivedEFT = realEstateTrustEFTs.find(
                (eft) => eft.type === "CommissionTransfer"
              );

              // If not found, look for a transaction with "Received From" in Commission Trust EFTs

              if (!commissionReceivedEFT) {
                commissionReceivedEFT = commissionTrustEFTs.find(
                  (eft) =>
                    eft.description &&
                    (eft.description.includes("Received From") ||
                      eft.description.includes("Received from") ||
                      eft.description.match(
                        /Received From:.*Commission Trust/i
                      ) ||
                      eft.description.includes("Commission Received"))
                );
              }

              // If still not found, check ledger entries from trade finalize (account 10004 with "Received From")

              if (
                !commissionReceivedEFT &&
                ledgerEntries &&
                ledgerEntries.length > 0
              ) {
                const commissionReceivedLedger = ledgerEntries.find(
                  (entry) =>
                    entry.accountNumber === "10004" &&
                    entry.debit > 0 &&
                    entry.description &&
                    (entry.description.includes("Received From") ||
                      entry.description.includes("Received from"))
                );

                if (commissionReceivedLedger) {
                  // Extract recipient from description (format: "Trade #: X - Address - Received From: Recipient")

                  let recipient = commissionReceivedLedger.payee || "";

                  if (!recipient && commissionReceivedLedger.description) {
                    const receivedFromMatch =
                      commissionReceivedLedger.description.match(
                        /Received From:\s*([^-]+?)(?:\s*-|$)/i
                      );

                    if (receivedFromMatch) {
                      recipient = receivedFromMatch[1].trim();
                    }
                  }

                  // Create EFT-like object from ledger entry

                  commissionReceivedEFT = {
                    eftNumber:
                      commissionReceivedLedger.eftNumber ||
                      commissionReceivedLedger.reference?.replace("EFT#", "") ||
                      "",

                    date:
                      commissionReceivedLedger.date ||
                      commissionReceivedLedger.chequeDate,

                    chequeDate:
                      commissionReceivedLedger.chequeDate ||
                      commissionReceivedLedger.date,

                    amount:
                      commissionReceivedLedger.debit ||
                      commissionReceivedLedger.amount,

                    recipient: recipient || "N/A",

                    type: "CommissionTransfer",

                    description: "Commission Received",

                    _id: commissionReceivedLedger._id,
                  };
                }
              }

              // If still not found, check by recipient name (common outside broker names)

              if (!commissionReceivedEFT) {
                commissionReceivedEFT = commissionTrustEFTs.find(
                  (eft) =>
                    eft.recipient &&
                    (eft.recipient.includes("Bestway") ||
                      eft.recipient.includes("Real Estate Ltd.,") ||
                      eft.recipient.includes("Brokerage"))
                );
              }

              if (commissionReceivedEFT) {
                // Create a formatted transaction with "Commission Received" as description

                const formattedEFT = {
                  ...commissionReceivedEFT,

                  description: "Commission Received",
                };

                // If the transaction was found in commissionTrustEFTs, remove it from the array first

                const remainingEFTs =
                  commissionReceivedEFT._id &&
                  commissionTrustEFTs.some(
                    (eft) => eft._id === commissionReceivedEFT._id
                  )
                    ? commissionTrustEFTs.filter(
                        (eft) => eft._id !== commissionReceivedEFT._id
                      )
                    : commissionTrustEFTs;

                displayCommissionTrustEFTs = [formattedEFT, ...remainingEFTs];
              }
            }

            if (
              !showBaseOfficeAndNetToOffice &&
              agentCalculationsFiltered &&
              agentCalculationsFiltered.length > 0
            ) {
              const selectedAgents = agentCalculationsFiltered;
              displayCommissionTrustEFTs = displayCommissionTrustEFTs.filter(
                (eft) => {
                  if (eft.type !== "AgentCommissionTransfer") return false;
                  return selectedAgents.some((selectedAgent) => {
                    const selectedAgentId =
                      selectedAgent?.agentId != null
                        ? selectedAgent.agentId.toString()
                        : "";
                    const selectedAgentName = (selectedAgent?.agentName || "")
                      .trim()
                      .toLowerCase();
                    const matchId =
                      selectedAgentId &&
                      eft.agentId != null &&
                      eft.agentId.toString() === selectedAgentId;
                    const matchName =
                      selectedAgentName &&
                      eft.agentName &&
                      (eft.agentName || "").trim().toLowerCase() ===
                        selectedAgentName;
                    const matchRecipient =
                      selectedAgentName &&
                      eft.recipient &&
                      (eft.recipient || "")
                        .trim()
                        .toLowerCase()
                        .includes(selectedAgentName);
                    return matchId || matchName || matchRecipient;
                  });
                },
              );
            }

            return displayCommissionTrustEFTs.length > 0
              ? `<table><thead><tr>

                    <th>EFT #</th><th>Date</th><th>Amount</th><th>Recipient</th><th>Type</th><th>Description</th>

                  </tr></thead><tbody>

                  ${displayCommissionTrustEFTs

                    .map(
                      (eft) => `<tr>

                        <td>${eft.eftNumber || ""}</td>

                        <td>${
                          eft.chequeDate || eft.date
                            ? formatDate(eft.chequeDate || eft.date)
                            : "-"
                        }</td>

                        <td>${formatCurrency(eft.amount || "")}</td>

                        <td>${eft.recipient || ""}</td>

                        <td>${getEFTTypeDisplayName(eft.type) || ""}</td>

                        <td>${eft.description || ""}</td>

                      </tr>`
                    )

                    .join("")}

                  </tbody></table>`
              : `<div class="text-gray-500">No Commission Trust Activity found for this trade.</div>`;
          })()}

        </div>



        <div class="section">

          <div class="section-title">Commission Trust Agreement</div>

          <div style="margin-bottom: 15px; font-size: 10px; line-height: 1.4;">

            It is understood between all parties that this agreement shall constitute a Commission Trust Agreement as set out in the contract.

          </div>

          <div style="margin-bottom: 15px; font-size: 10px; line-height: 1.4;">

            To the best of my knowledge the above information is correct. Dated at Moncton on ${currentDate}

          </div>

          ${(agentDetailsForPrint && agentDetailsForPrint.length > 0
            ? agentDetailsForPrint
            : []
          )
            .map(
              (agent) => `
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
            <div style="font-size: 10px;">
              ${agent.agentName || "Agent Name"}, ${agent.agentId ?? "Agent Number"}
            </div>
            <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>
          </div>
`,
            )
            .join("")}
          ${
            !agentDetailsForPrint || agentDetailsForPrint.length === 0
              ? `
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
            <div style="font-size: 10px;">Agent Name, Agent Number</div>
            <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>
          </div>
`
              : ""
          }

          <div style="display: flex; justify-content: space-between; align-items: center;">

            <div style="font-size: 10px;">

              Broker of Record: Bestway Real Estate Ltd., Brokerage

            </div>

            <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>

          </div>

        </div>

        <div class="no-print" style="position: fixed; top: 20px; right: 20px;">

          <button onclick="window.print()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer;">Print</button>

          ${
            useCurrentWindow
              ? ""
              : '<button onclick="window.close()" style="padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 5px; cursor: pointer; margin-left: 10px;">Close</button>'
          }

        </div>

      </body>

      </html>

    `;

      printWindow.document.write(printContent);

      printWindow.document.close();

      // Wait for content to load then automatically print

      setTimeout(() => {
        try {
          printWindow.print();

          // Close the window after a delay to allow printing to complete

          if (!useCurrentWindow) {
            setTimeout(() => {
              printWindow.close();
            }, 1000);
          }

          // Show success message

          if (!useCurrentWindow) {
            setTimeout(() => {
              alert("Print dialog opened successfully!");
            }, 100);
          }
        } catch (printError) {
          console.error("Print error:", printError);

          // If automatic print fails, keep the window open for manual printing

          if (!useCurrentWindow) {
            printWindow.focus();
          }

          alert(
            "Print dialog failed to open. The report is ready for manual printing."
          );
        }
      }, 500);
    } catch (error) {
      console.error("Error generating print report:", error);

      alert("Error generating print report. Please try again.");
    } finally {
      setIsPrinting(false);
    }
  };

  // Helper to generate transaction details for this trade

  const getTransactionDetails = () => {
    // You may want to adjust these values to match your backend logic

    const commissionIncome = trade.commission?.commissionIncomeRows?.[0] || {};

    const firstAgent = trade.agentCommissionList?.[0] || {};

    const address = `${get(trade, "keyInfo.streetNumber") || ""} ${
      get(trade, "keyInfo.streetName") || ""
    }`.trim();

    const commissionDescription = `Trade #${trade.tradeNumber} - ${address}`;

    const commissionDate = get(trade, "keyInfo.closeDate") || "-";

    const taxR = getTradeTaxRate(trade);

    const hstInputTaxCredit =
      parseFloat(commissionIncome.sellingAmount || 0) * taxR;

    const agentAmount = parseFloat(firstAgent.amount || 0);

    const agentNetCommission = parseFloat(firstAgent.netCommission || 0);

    const feesDeducted = parseFloat(firstAgent.feesDeducted || 0);

    const officeExpensesTotal = parseFloat(
      firstAgent.officeExpensesTotal || 0,
    );

    const totalFees = parseFloat(firstAgent.totalFees || 0);

    const combinedDeductionTax =
      totalFees > 0
        ? Math.round((totalFees - feesDeducted) * 100) / 100
        : Math.round(feesDeducted * taxR * 100) / 100;

    const credit12100 = Math.min(
      Math.max(0, Math.round(officeExpensesTotal * 100) / 100),
      Math.max(0, combinedDeductionTax),
    );

    const credit23001PlanHst =
      Math.round((combinedDeductionTax - credit12100) * 100) / 100;

    // Main transactions

    const mainTxns = [
      {
        date: commissionDate,

        accountNumber: "40100",

        accountName: "Commission Income",

        debit: "",

        credit: commissionIncome.listingAmount,

        description: commissionDescription,
      },

      {
        date: commissionDate,

        accountNumber: "23001",

        accountName: "HST Input Tax Credit",

        debit: commissionIncome.sellingAmount * taxR,

        credit: "",

        description: commissionDescription,
      },

      {
        date: commissionDate,

        accountNumber: "23000",

        accountName: "HST Collected",

        debit: "",

        credit: commissionIncome.sellingAmount * taxR,

        description: commissionDescription,
      },

      {
        date: commissionDate,

        accountNumber: "50100",

        accountName: "Agent's Commission",

        debit: agentAmount,

        credit: "",

        description: commissionDescription,
      },

      {
        date: commissionDate,

        accountNumber: "21500",

        accountName: "Commission Payable",

        debit: "",

        credit: agentNetCommission,

        description: commissionDescription,
      },

      {
        date: commissionDate,

        accountNumber: "44100",

        accountName: "Fee Deducted Income",

        debit: "",

        credit: feesDeducted,

        description: commissionDescription,
      },

      ...(credit23001PlanHst > 0
        ? [
            {
              date: commissionDate,
              accountNumber: "23001",
              accountName: "HST Input Tax Credit",
              debit: "",
              credit: credit23001PlanHst.toFixed(2),
              description: commissionDescription,
            },
          ]
        : []),

      ...(credit12100 > 0
        ? [
            {
              date: commissionDate,
              accountNumber: "12100",
              accountName: "A/R - Agt's Recoverable- Gen Exp",
              debit: "",
              credit: credit12100.toFixed(2),
              description: commissionDescription,
            },
          ]
        : []),

      {
        date: commissionDate,

        accountNumber: "12200",

        accountName: "A/R - Commission From Deals",

        debit: commissionIncome.listingAmount
          ? (
              parseFloat(commissionIncome.listingAmount) + hstInputTaxCredit
            ).toFixed(2)
          : "",

        credit: "",

        description: commissionDescription,
      },
    ];

    // Next 2 transactions (Payment Received)

    const paymentTxns = [
      {
        date: commissionDate,

        accountNumber: "10004",

        accountName: "CASH - COMMISSION TRUST ACCOUNT",

        debit: agentNetCommission,

        credit: "",

        description: `Trade #${trade.tradeNumber}, Paid to: Agent`,
      },

      {
        date: commissionDate,

        accountNumber: "21500",

        accountName: "Commission Payable",

        debit: agentNetCommission,

        credit: "",

        description: `Trade #${trade.tradeNumber}, Paid to: Agent`,
      },

      {
        date: commissionDate,

        accountNumber: "10004",

        accountName: "CASH - COMMISSION TRUST ACCOUNT",

        debit: feesDeducted,

        credit: "",

        description: `Trade #${trade.tradeNumber}, Paid to: Bestway Real Estate Ltd., Brokerage`,
      },

      {
        date: commissionDate,

        accountNumber: "10001",

        accountName: "CASH - CURRENT ACCOUNT",

        debit: "",

        credit: feesDeducted,

        description: `Trade #${trade.tradeNumber}, Paid to: Bestway Real Estate Ltd., Brokerage`,
      },
    ];

    return { mainTxns, paymentTxns };
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-4xl w-full relative overflow-y-auto max-h-[90vh]">
        {/* Top-right action buttons */}

        <div className="flex gap-3 absolute top-4 right-6 z-10">
          <button
            type="button"
            onClick={() => setShowNotesModal(true)}
            className="bg-amber-700 text-white px-4 py-2 rounded hover:bg-amber-800 transition-colors"
          >
            Note
          </button>
          <button
            onClick={() => {
              const agents = (trade?.agentCommissionList || []).filter(
                (a) =>
                  (a.classification || "").toUpperCase() !== "REFERRAL",
              );
              if (agents.length > 1) {
                setShowPrintAgentModal(true);
              } else {
                handlePrint(null);
              }
            }}
            disabled={isPrinting}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPrinting ? "Generating..." : "Print"}
          </button>

          <button
            onClick={() => setShowEditModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
          >
            Edit
          </button>

          <button
            className="text-2xl text-gray-500 hover:text-gray-700 px-2"
            onClick={onClose}
            style={{ lineHeight: 1 }}
          >
            &times;
          </button>
        </div>

        {showEditModal ? (
          <TradeEditModal
            trade={trade}
            onClose={() => setShowEditModal(false)}
            onUpdate={onUpdate}
          />
        ) : (
          <>
            <h2 className="text-2xl font-bold mb-6 text-yellow-700 mt-8">
              Trade Details: #{trade.tradeNumber}
            </h2>

            {/* Address Section */}

            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Address</h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>
                  <span className="text-gray-500 font-normal">Street #</span>

                  <br />

                  {get(trade, "keyInfo.streetNumber")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">Street Name</span>

                  <br />

                  {get(trade, "keyInfo.streetName")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">Unit</span>

                  <br />

                  {get(trade, "keyInfo.unit")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">City</span>

                  <br />

                  {get(trade, "keyInfo.city")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">Province</span>

                  <br />

                  {get(trade, "keyInfo.province")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">Postal Code</span>

                  <br />

                  {get(trade, "keyInfo.postalCode")}
                </div>
              </div>
            </div>

            {/* Dates Section */}

            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Important Dates</h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>
                  <span className="text-gray-500 font-normal">Offer Date</span>

                  <br />

                  {get(trade, "keyInfo.offerDate")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">Firm Date</span>

                  <br />

                  {get(trade, "keyInfo.firmDate")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">Close Date</span>

                  <br />

                  {get(trade, "keyInfo.closeDate")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">Entry Date</span>

                  <br />

                  {get(trade, "keyInfo.entryDate")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">
                    Finalized Date
                  </span>

                  <br />

                  {get(trade, "keyInfo.finalizedDate")}
                </div>
              </div>
            </div>

            {/* Key Information Section */}

            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Key Information</h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>
                  <span className="text-gray-500 font-normal">
                    Listing Number
                  </span>

                  <br />

                  {get(trade, "keyInfo.listingNumber")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">Sell Price</span>

                  <br />

                  {get(trade, "keyInfo.sellPrice")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">
                    Classification
                  </span>

                  <br />

                  {get(trade, "keyInfo.classification")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">Firm</span>

                  <br />

                  {get(trade, "keyInfo.firm")}
                </div>
              </div>
            </div>

            {/* Commission */}

            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Commission</h3>

              <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
                <div>
                  <span className="text-gray-500 font-normal">List %</span>

                  <br />

                  {get(trade, "keyInfo.listCommission")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">Sell %</span>

                  <br />

                  {get(trade, "keyInfo.sellCommission")}
                </div>
              </div>
            </div>

            {/* Property Details */}

            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Property Details</h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>
                  <span className="text-gray-500 font-normal">
                    Property Type
                  </span>

                  <br />

                  {get(trade, "keyInfo.propertyType")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">Status</span>

                  <br />

                  {get(trade, "keyInfo.status")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">MLS #</span>

                  <br />

                  {get(trade, "keyInfo.mlsNumber")}
                </div>

                <div>
                  <span className="text-gray-500 font-normal">We Manage</span>

                  <br />

                  {get(trade, "keyInfo.weManage")}
                </div>
              </div>
            </div>

            {/* People Information Section */}

            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">People Information</h3>

              {trade.people && trade.people.length > 0 ? (
                <table className="min-w-full border border-gray-300 mb-2 text-sm">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Type
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        First Name
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Last Name
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Primary Phone
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Secondary Phone
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Email
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Company Name
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {trade.people.map((person, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">{person.type}</td>

                        <td className="border px-2 py-1">
                          {person.firstName || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {person.lastName || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {person.primaryPhone || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {person.secondaryPhone || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {person.email || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {person.companyName || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-gray-500">
                  No people information available.
                </div>
              )}
            </div>

            {/* Outside Brokers Section */}

            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Outside Brokers</h3>

              {trade.outsideBrokers && trade.outsideBrokers.length > 0 ? (
                <table className="min-w-full border border-gray-300 mb-2 text-sm">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Type
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        End
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        First Name
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Last Name
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Company
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Phone Number
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {trade.outsideBrokers.map((broker, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">
                          {broker.type || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {broker.end || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {broker.firstName || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {broker.lastName || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {broker.company || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {broker.primaryPhone || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-gray-500">
                  No outside brokers information available.
                </div>
              )}
            </div>

            {/* Outside Brokers Commission Details Section */}

            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">
                Outside Brokers Commission Details
              </h3>

              {trade.commission &&
              trade.commission.outsideBrokersRows &&
              trade.commission.outsideBrokersRows.length > 0 ? (
                <table className="min-w-full border border-gray-300 mb-2 text-sm">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Agent Name
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Brokerage
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        End
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Selling Amount
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Tax
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Total
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {trade.commission.outsideBrokersRows.map((broker, idx) => {
                      const getEndValueForPrint = (b) => {
                        // First check the broker's end from outsideBrokers array

                        const matchingBroker1 = trade.outsideBrokers?.find(
                          (ob) => {
                            const outsideBrokerName =
                              `${ob.firstName} ${ob.lastName}`.trim();

                            return (
                              outsideBrokerName === b.agentName ||
                              ob.company === b.brokerage
                            );
                          }
                        );

                        // If we found a matching broker in outsideBrokers array, use their end type

                        if (matchingBroker1) {
                          if (
                            matchingBroker1.end

                              ?.toLowerCase()

                              .includes("listing") ||
                            matchingBroker1.type

                              ?.toLowerCase()

                              .includes("listing")
                          ) {
                            return "Listing";
                          }
                        }

                        // If no match found or not listing, check the commission broker's end/type

                        if (
                          b.end?.toLowerCase().includes("listing") ||
                          b.type?.toLowerCase().includes("listing")
                        ) {
                          return "Listing";
                        }

                        return "Selling";
                      };

                      return (
                        <tr key={idx}>
                          <td className="border px-2 py-1">
                            {broker.agentName || "-"}
                          </td>

                          <td className="border px-2 py-1">
                            {broker.brokerage || "-"}
                          </td>

                          <td className="border px-2 py-1">
                            {getEndValueForPrint(broker)}
                          </td>

                          <td className="border px-2 py-1">
                            {formatCurrency(broker.sellingAmount)}
                          </td>

                          <td className="border px-2 py-1">
                            {formatCurrency(broker.tax)}
                          </td>

                          <td className="border px-2 py-1">
                            {formatCurrency(broker.total)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="text-gray-500">
                  No outside brokers commission details available.
                </div>
              )}
            </div>

            {/* Trust Records Section */}

            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Trust Records</h3>

              {trade.trustRecords && trade.trustRecords.length > 0 ? (
                <table className="min-w-full border border-gray-300 mb-2 text-sm">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        We Hold
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Received
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Received From
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Held By
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Deposit Date
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Reference
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Amount
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Payment Type
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {trade.trustRecords.map((t, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">{t.weHold || "-"}</td>

                        <td className="border px-2 py-1">
                          {t.received || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {t.receivedFrom || "-"}
                        </td>

                        <td className="border px-2 py-1">{t.heldBy || "-"}</td>

                        <td className="border px-2 py-1">
                          {t.depositDate || t.date || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {t.reference || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {formatCurrency(t.amount || "-")}
                        </td>

                        <td className="border px-2 py-1">
                          {t.paymentType || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-gray-500">No trust records.</div>
              )}
            </div>

            {/* Conditions Section */}

            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Conditions</h3>

              {trade.conditions && trade.conditions.length > 0 ? (
                <table className="min-w-full border border-gray-300 mb-2 text-sm">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Condition
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Due Date
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Condition Met Date
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {trade.conditions.map((condition, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">
                          {condition.conditionText || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {condition.dueDate || "-"}
                        </td>

                        <td className="border px-2 py-1">
                          {condition.conditionMetDate || "Not met"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-gray-500">No conditions available.</div>
              )}
            </div>

            {/* Commission Section */}

            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Commission Details</h3>

              {trade.commission && (
                <>
                  <div className="mb-4">
                    <h4 className="text-gray-700 mb-1">Sale Closing Rows</h4>

                    {renderTable(trade.commission.saleClosingRows)}
                  </div>

                  <div className="mb-4">
                    <h4 className="text-gray-700 mb-1">
                      Commission Income Rows
                    </h4>

                    {renderTable(trade.commission.commissionIncomeRows)}
                  </div>

                  <div className="mb-4">
                    <h4 className="text-gray-700 mb-1">Outside Brokers Rows</h4>

                    {renderTable(trade.commission.outsideBrokersRows)}
                  </div>
                </>
              )}
            </div>

            {/* Agent Commission Info Section */}

            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">
                Agent Commission Info
              </h3>

              {trade.agentCommissionList &&
              trade.agentCommissionList.length > 0 ? (
                <table className="min-w-full border border-gray-300 mb-2 text-sm">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Agent Name
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Classification
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Amount
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Net Commission
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Lead
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Fee Info
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Total Fees
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Office Exp.
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Tax
                      </th>

                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Total
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {trade.agentCommissionList.map((a, idx) => {
                      const isCoOp =
                        (trade.keyInfo?.classification || "") ===
                        "CO-OPERATING SIDE";
                      const officeLines = buildAgentOfficeExpenseSheetLines(
                        a,
                        isCoOp,
                      );
                      return (
                        <React.Fragment key={idx}>
                          <tr>
                            <td className="border px-2 py-1">{a.agentName}</td>
                            <td className="border px-2 py-1">
                              {a.classification}
                            </td>
                            <td className="border px-2 py-1">
                              {formatCurrency(a.amount)}
                            </td>
                            <td className="border px-2 py-1">
                              {formatCurrency(a.netCommission)}
                            </td>
                            <td className="border px-2 py-1">{a.lead}</td>
                            <td className="border px-2 py-1">
                              {getPaymentPlanDisplayName(a.feeInfo)}
                            </td>
                            <td className="border px-2 py-1">
                              {formatCurrency(a.totalFees)}
                            </td>
                            <td className="border px-2 py-1">
                              {parseFloat(a.officeExpensesTotal || 0) > 0
                                ? formatCurrency(a.officeExpensesTotal)
                                : "-"}
                            </td>
                            <td className="border px-2 py-1">
                              {formatCurrency(a.tax)}
                            </td>
                            <td className="border px-2 py-1">
                              {formatCurrency(a.total)}
                            </td>
                          </tr>
                          {officeLines.map((line) => (
                            <tr key={`${idx}-${line.key}`} className="bg-gray-50">
                              <td className="border px-2 py-1 pl-6 text-gray-700">
                                {line.label}
                              </td>
                              <td className="border px-2 py-1" colSpan={2} />
                              <td className="border px-2 py-1 text-right">
                                {formatCurrency(line.subTotal)}
                              </td>
                              <td className="border px-2 py-1" colSpan={3} />
                              <td className="border px-2 py-1 text-right">
                                {formatCurrency(line.hst)}
                              </td>
                              <td className="border px-2 py-1 text-right">
                                {formatCurrency(line.total)}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="text-gray-500">No agent commission data.</div>
              )}

              {tradeDetailsExtras.feesDeductionRows.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-gray-700 font-medium mb-2">
                    Fees Deduction
                  </h4>
                  <table className="min-w-full border border-gray-300 mb-2 text-sm">
                    <thead>
                      <tr>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Agent
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Fee Type
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Deducted Fee
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Taxed
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tradeDetailsExtras.feesDeductionRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className="border px-2 py-1">
                            {row.agentName || "-"}
                          </td>
                          <td className="border px-2 py-1">{row.feeType}</td>
                          <td className="border px-2 py-1">
                            {formatCurrency(row.deductedFee)}
                          </td>
                          <td className="border px-2 py-1">{row.taxed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tradeDetailsExtras.netToOffice && (
                <div className="mt-4 bg-gray-50 border border-gray-200 rounded-md p-4">
                  <h4 className="text-gray-700 font-medium mb-2">
                    Net to Office
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Sub-total</span>
                      <br />
                      <strong>
                        {formatCurrency(
                          tradeDetailsExtras.netToOffice.netToOfficeSubTotal,
                        )}
                      </strong>
                    </div>
                    <div>
                      <span className="text-gray-500">HST</span>
                      <br />
                      <strong>
                        {formatCurrency(
                          tradeDetailsExtras.netToOffice.netToOfficeHST,
                        )}
                      </strong>
                    </div>
                    <div>
                      <span className="text-gray-500">Total</span>
                      <br />
                      <strong>
                        {formatCurrency(
                          tradeDetailsExtras.netToOffice.netToOfficeTotal,
                        )}
                      </strong>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* EFT Payment Tables Section */}

            <TradePaymentDetailsView trade={trade} />

            {/* Transaction Details Section */}

            {/* Removed Trade Finalize and Payment Received tables as requested */}
          </>
        )}
      </div>

      {showNotesModal && (
        <TradeNotesModal
          trade={{ ...trade, notes: tradeNotes }}
          onClose={() => setShowNotesModal(false)}
          onSaved={(savedNotes) => {
            setTradeNotes(savedNotes);
            if (typeof onUpdate === "function") {
              onUpdate({ ...trade, notes: savedNotes });
            }
          }}
        />
      )}

      {showPrintAgentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              Select agent for Trade Record Sheet
            </h3>

            <div className="space-y-2 mb-4">
              {tradeRecordSheetAgentPickerEntries.map((entry) => (
                <button
                  key={entry.key}
                  type="button"
                  onClick={() => {
                    setShowPrintAgentModal(false);
                    handlePrint(entry.printIndex);
                  }}
                  className="w-full text-left px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition-colors"
                >
                  {entry.label}
                </button>
              ))}

              <button
                type="button"
                onClick={() => {
                  setShowPrintAgentModal(false);
                  handlePrint(null);
                }}
                className="w-full text-left px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition-colors font-medium"
              >
                All agents
              </button>
            </div>

            <button
              type="button"
              onClick={() => setShowPrintAgentModal(false)}
              className="w-full px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeDetailsModal;
